/**
 * WalletUserAvatar
 * Flutter source: wallet_screen.dart - the leading `CircleBorderAvatar` wrapped in a
 * `GestureDetector` → `navigateToUserDetailsScreen(userId)` used by every transaction
 * item, plus the `if (widget.isFromAdmin) ...[ ... ]` trailing avatar.
 *
 * Shared clickable round user avatar for wallet transaction items. Tapping it navigates
 * to the user's details. Centralizes the avatar sizing/radius/fit, the navigation, and
 * the profile-image URL so the leading inline avatars (subscription, gift, unlock) and
 * the trailing admin avatar all render the same markup.
 */
import React from 'react';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { UserUtils } from '@/domain/utils/userUtils';
import { Navigator } from '@/presentation/routing/Navigator';
import { qaAttr } from '@/presentation/utils/qaAttr';

interface WalletUserAvatarProps {
  userId: string;
  /** Accessible label for the avatar image (typically the user's name). */
  alt?: string;
  /** Optional `data-qa-id` for the clickable wrapper. */
  qaId?: string;
}

export const WalletUserAvatar: React.FC<WalletUserAvatarProps> = ({ userId, alt, qaId }) => {
  const responsive = useResponsiveDesign();

  // Guard the navigation so a missing/placeholder id is a no-op rather than a
  // route fall-through to home. `navigateToUserDetails('')` would push `/` (home).
  // The subscription item falls back to `''` when `usersInfo.peerUserId` is absent
  // (the web entity types it optional); Flutter's `UsersInfo.peerUserId` is a
  // non-nullable String defaulting to `"_"`, so it never routes to home. Treat
  // both the empty fallback and Flutter's `"_"` placeholder as a no-op here.
  const handleClick = () => {
    if (userId && userId !== '_') {
      Navigator.navigateToUserDetails(userId);
    }
  };

  return (
    <div
      style={{ cursor: 'pointer', flexShrink: 0 }}
      onClick={handleClick}
      {...(qaId ? qaAttr({ id: qaId }) : {})}
    >
      <CachedImage
        src={UserUtils.generateUserProfileImageUrl(userId)}
        alt={alt}
        style={{
          width: responsive.avatar.medium,
          height: responsive.avatar.medium,
          borderRadius: responsive.borderRadius.round,
          objectFit: 'cover',
        }}
      />
    </div>
  );
};
